import { useEffect, useMemo, useRef, useState } from 'react';
import type React from 'react';

/**
 * Langdruck („gedrückt halten“) für Kacheln und Listenzeilen der
 * Mehrfachauswahl (Paket L, 30.09.).
 *
 * - Finger/Stift: LANGDRUCK_MS halten → onHalten; der Klick danach wird
 *   geschluckt. Wandert der Finger mehr als LANGDRUCK_TOLERANZ_PX (Scrollen),
 *   zählt es nicht.
 * - Maus: Rechtsklick (contextmenu) oder Strg-/Umschalt-/Cmd-Klick = halten.
 * - Tastatur: Umschalt+Enter = halten; ein Tastatur-Klick wird nie geschluckt.
 * - Android meldet einen Langdruck zusätzlich als contextmenu — der zählt
 *   nur einmal.
 * - Kurzer Tipp → onKurz.
 */

export const LANGDRUCK_MS = 450;
export const LANGDRUCK_TOLERANZ_PX = 10;

export interface LangdruckAktionen {
    onHalten: () => void;
    onKurz: () => void;
}

/** Zeitgeber (im Test ersetzbar). */
export interface LangdruckUhr {
    setzen: (fn: () => void, ms: number) => unknown;
    loeschen: (id: unknown) => void;
}

export interface Langdruck {
    /** taste: 0 = Haupttaste/Finger; andere Tasten starten nichts. */
    druecken: (x: number, y: number, taste: number) => void;
    bewegen: (x: number, y: number) => void;
    loslassen: () => void;
    klick: (p: { modifikator: boolean; tastatur: boolean }) => void;
    kontextmenue: () => void;
    aufraeumen: () => void;
}

/** Reine Steuerung; `aktionen` wird bei jedem Auslösen neu gelesen. */
export function langdruckSteuerung(aktionen: () => LangdruckAktionen, uhr: LangdruckUhr): Langdruck {
    let zeitgeber: unknown = null;
    let start: { x: number; y: number } | null = null;
    let ausgeloest = false;

    const stoppen = () => {
        if (zeitgeber !== null) uhr.loeschen(zeitgeber);
        zeitgeber = null;
        start = null;
    };

    return {
        druecken(x, y, taste) {
            stoppen();
            ausgeloest = false;
            if (taste !== 0) return;
            start = { x, y };
            zeitgeber = uhr.setzen(() => {
                zeitgeber = null;
                start = null;
                ausgeloest = true;
                aktionen().onHalten();
            }, LANGDRUCK_MS);
        },
        bewegen(x, y) {
            if (zeitgeber === null || !start) return;
            if (Math.hypot(x - start.x, y - start.y) > LANGDRUCK_TOLERANZ_PX) stoppen();
        },
        loslassen() {
            stoppen();
        },
        klick({ modifikator, tastatur }) {
            const geschluckt = ausgeloest && !tastatur;
            ausgeloest = false;
            if (geschluckt) return;
            if (modifikator) aktionen().onHalten();
            else aktionen().onKurz();
        },
        kontextmenue() {
            if (ausgeloest) return;
            stoppen();
            ausgeloest = true;
            aktionen().onHalten();
        },
        aufraeumen() {
            stoppen();
        },
    };
}

const BROWSER_UHR: LangdruckUhr = {
    setzen: (fn, ms) => setTimeout(fn, ms),
    loeschen: (id) => clearTimeout(id as ReturnType<typeof setTimeout>),
};

/** Kurzes Rütteln beim Halten, wo das Gerät es kann (nie werfen). */
function ruetteln(): void {
    try {
        if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') navigator.vibrate(12);
    } catch {
        /* egal */
    }
}

export interface LangdruckHandler {
    onPointerDown: (e: React.PointerEvent) => void;
    onPointerMove: (e: React.PointerEvent) => void;
    onPointerUp: () => void;
    onPointerCancel: () => void;
    onPointerLeave: () => void;
    onContextMenu: (e: React.MouseEvent) => void;
    onClick: (e: React.MouseEvent) => void;
}

/** Handler für einen Knopf mit Langdruck (auf den Knopf spreizen). */
export function useLangdruck(aktionen: LangdruckAktionen): LangdruckHandler {
    const aktuell = useRef(aktionen);
    useEffect(() => {
        aktuell.current = aktionen;
    });
    const [steuerung] = useState(() =>
        langdruckSteuerung(
            () => ({
                onHalten: () => {
                    ruetteln();
                    aktuell.current.onHalten();
                },
                onKurz: () => aktuell.current.onKurz(),
            }),
            BROWSER_UHR,
        ),
    );
    useEffect(() => () => steuerung.aufraeumen(), [steuerung]);
    return useMemo(
        () => ({
            onPointerDown: (e) => steuerung.druecken(e.clientX, e.clientY, e.button),
            onPointerMove: (e) => steuerung.bewegen(e.clientX, e.clientY),
            onPointerUp: () => steuerung.loslassen(),
            onPointerCancel: () => steuerung.loslassen(),
            onPointerLeave: () => steuerung.loslassen(),
            onContextMenu: (e) => {
                e.preventDefault();
                steuerung.kontextmenue();
            },
            onClick: (e) =>
                steuerung.klick({ modifikator: e.shiftKey || e.ctrlKey || e.metaKey, tastatur: e.detail === 0 }),
        }),
        [steuerung],
    );
}

/** Klassen gegen Textauswahl und iOS-Kontextblase beim Halten. */
export const LANGDRUCK_KLASSEN = 'select-none [-webkit-touch-callout:none]';
